/**
 * Pure H OS · Iter 136 · Wave B · Consolidated Workspace Inbox.
 *
 * Merges Executive Inbox + Notifications into one 6-tab surface under
 * `/pureh/workspace/inbox`. The dedicated `/pureh/notifications`
 * historical page remains reachable for full-archive audit needs.
 *
 * Tabs:
 *   1. Assigned to Me   — items owned by the current user
 *   2. Approvals        — pending approvals across modules
 *   3. Notifications    — canonical stream
 *   4. Mentions         — @-mentions
 *   5. System Alerts    — system-level events
 *   6. Archived         — resolved / archived items
 */
import { useState } from "react";
import { Bell, CheckSquare, MessageSquare, AtSign, ShieldAlert, Archive } from "lucide-react";
import Notifications from "./Notifications";
import ExecutiveInbox from "./ExecutiveInbox";

type InboxTab = "assigned" | "approvals" | "notifications" | "mentions" | "alerts" | "archived";

const TABS: { key: InboxTab; label: string; icon: any; testId: string }[] = [
  { key: "assigned",      label: "Assigned to Me", icon: CheckSquare,   testId: "inbox-tab-assigned" },
  { key: "approvals",     label: "Approvals",      icon: MessageSquare, testId: "inbox-tab-approvals" },
  { key: "notifications", label: "Notifications",  icon: Bell,          testId: "inbox-tab-notifications" },
  { key: "mentions",      label: "Mentions",       icon: AtSign,        testId: "inbox-tab-mentions" },
  { key: "alerts",        label: "System Alerts",  icon: ShieldAlert,   testId: "inbox-tab-alerts" },
  { key: "archived",      label: "Archived",       icon: Archive,       testId: "inbox-tab-archived" },
];

export default function WorkspaceInbox() {
  const [tab, setTab] = useState<InboxTab>("assigned");

  // Wave D · Keep all tabs mounted so switching preserves scroll/state and
  // doesn't re-run their data fetching. Only the active tab is visible.
  return (
    <div className="space-y-6" data-testid="workspace-inbox">
      <header className="space-y-1">
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Workspace</div>
        <h1 className="text-2xl font-semibold text-slate-900">Inbox</h1>
        <p className="text-sm text-slate-600 max-w-2xl">
          All assignments, approvals, mentions and notifications in a single place.
          The Notifications historical page remains reachable at{" "}
          <a href="/pureh/notifications" className="underline decoration-dotted">/pureh/notifications</a>.
        </p>
      </header>

      <nav className="flex flex-wrap gap-1 border-b border-slate-200" data-testid="inbox-tabs">
        {TABS.map(({ key, label, icon: Icon, testId }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={testId}
              className={
                "flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors " +
                (active
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-800")
              }
            >
              <Icon size={14} /> {label}
            </button>
          );
        })}
      </nav>

      <div>
        <div hidden={tab !== "assigned"} data-testid="inbox-body-assigned"><ExecutiveInbox /></div>
        <div hidden={tab !== "approvals"} data-testid="inbox-body-approvals"><NestedIframeMode section="approvals" /></div>
        <div hidden={tab !== "notifications"} data-testid="inbox-body-notifications"><Notifications /></div>
        <div hidden={tab !== "mentions"} data-testid="inbox-body-mentions"><EmptyState label="Mentions" hint="You have no @-mentions yet." /></div>
        <div hidden={tab !== "alerts"} data-testid="inbox-body-alerts"><EmptyState label="System alerts" hint="No system alerts in the last 30 days." /></div>
        <div hidden={tab !== "archived"} data-testid="inbox-body-archived"><EmptyState label="Archived" hint="Archived items land here after 30 days." /></div>
      </div>
    </div>
  );
}

function EmptyState({ label, hint }: { label: string; hint: string }) {
  return (
    <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500" data-testid="inbox-empty">
      <div className="text-sm font-medium text-slate-700">{label}</div>
      <div className="text-xs mt-1">{hint}</div>
    </div>
  );
}

function NestedIframeMode({ section }: { section: string }) {
  // Rendering the Approvals workspace inline via iframe would require
  // shared auth cookies; instead we deep-link straight to it so the
  // existing UnifiedApprovals page keeps its own state machine intact.
  return (
    <div className="rounded border border-slate-200 bg-white p-6" data-testid={`inbox-${section}-cta`}>
      <div className="text-sm text-slate-700 mb-3">
        Open the full Approvals workspace to review pending items.
      </div>
      <a
        href="/pureh/approvals"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-slate-900 text-white text-sm hover:bg-slate-800"
        data-testid={`inbox-${section}-open`}
      >
        Open Approvals →
      </a>
    </div>
  );
}
